/**
 * Which agents there are, and which one is on screen.
 *
 * THE ADDRESS NAMES THE AGENT: `/eco-boats/calls/315`. The first path segment is the agent's
 * slug - unique across the deployment and never changed once made - and everything under it
 * belongs to that agent. There is no `?agent=` and no "current agent" on the server: the
 * Worker hands over the whole list once (`GET /v1/agents`), `AgentScope` in `App.tsx` finds
 * the slug in it, and every request a page makes names that agent by id. Switching agents is
 * a navigation, not a fetch.
 *
 * TWO CONTEXTS, BECAUSE THEY HAVE TWO LIFETIMES. The list is the app's: it is asked for once
 * inside the session's gate, and the switcher, the `/` redirect and the scope all read it.
 * The current agent is the route's: it exists only under `/:agent`, so a screen outside one
 * (`/agents/new`) reads `current: null` rather than a guess.
 *
 * ONE AGENT'S TIME, MONEY AND WORDS FOR EVERY SCREEN. A timestamp on the calls list and the
 * same timestamp on the call dialog must be in one clock, and a band drawn as "Hot lead" on
 * the overview must not be "hot" in the filter bar - which is why they are read from here and
 * never fetched per page. What this file does NOT do is enforce anything: the Worker resolves
 * the agent on every request and 404s one the caller may not have, whatever this file believes.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, ApiError, type Agent, type AgentsPage } from "./api";

interface AgentsState {
  /** Every agent the reader may reach, in the switcher's order. Empty until the first answer. */
  agents: Agent[];
  /** True until the list has been answered once. A `reload` keeps the old list on screen. */
  loading: boolean;
  error: ApiError | null;
  /**
   * Ask again - after a rename, a view switch, a new agent - and settle once the answer is
   * in. AWAITABLE because a caller about to navigate to an agent that was just made has to
   * know the list holds it first, or the address it lands on is "No such agent" for a beat.
   */
  reload: () => Promise<void>;
}

const Agents = createContext<AgentsState>({
  agents: [],
  loading: true,
  error: null,
  reload: async () => {},
});

export function AgentsProvider({ children }: { children: ReactNode }) {
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  // The newest request wins: two reloads in flight may answer in either order.
  const latest = useRef(0);

  const reload = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const page = await api<AgentsPage>("/v1/agents");
      if (ticket !== latest.current) return;
      setAgents(page.agents);
      setError(null);
    } catch (err) {
      if (ticket !== latest.current) return;
      setError(err instanceof ApiError ? err : new ApiError(0, (err as Error).message ?? "Failed"));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <Agents.Provider value={{ agents: agents ?? [], loading: agents === null && error === null, error, reload }}>
      {children}
    </Agents.Provider>
  );
}

export function useAgents(): AgentsState {
  return useContext(Agents);
}

const Current = createContext<Agent | null>(null);

/** Mounted by the `/:agent` route once the slug has been found in the list. */
export function CurrentAgentProvider({ agent, children }: { agent: Agent; children: ReactNode }) {
  return <Current.Provider value={agent}>{children}</Current.Provider>;
}

/**
 * The agent on screen, or null outside an agent's address, with the list beside it.
 *
 * NULL IS NOT AN ERROR. Components shared with the unscoped screens read `current?.timezone`
 * and fall back to the browser's clock, which is an honest default; a page that only exists
 * under `/:agent` uses `useAgent()` instead and never sees the null.
 */
export function useCurrentAgent(): AgentsState & { current: Agent | null } {
  return { ...useAgents(), current: useContext(Current) };
}

/** The agent on screen, for a page that is only ever rendered under `/:agent`. */
export function useAgent(): Agent {
  const agent = useContext(Current);
  if (!agent) throw new Error("useAgent() outside an agent's address - render it under /:agent");
  return agent;
}
